import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchCategories } from '../../services/apiService';
import { pickCategoryName } from '../../utils/categoryUtils';
import {
  FaPills,
  FaLungs,
  FaCapsules,
  FaSprayCan,
  FaHandSparkles,
  FaPalette,
} from 'react-icons/fa';

// Reuse a subset of the MoreInfo configuration for a compact bar
const categoryDisplayMap = {
  PainFever: 'Schmerzen & Fieber',
  FluColds: 'Grippe & Erkältung',
  VitaminsSupplements: 'Vitamine',
  HygieneDisinfection: 'Hygiene & Desinfektion',
  SkinHairNails: 'Haut, Haare & Nägel',
  FacialSkinCareMakeUp: 'Gesichtspflege & Make-Up',
};

const categoryIconMap = {
  PainFever: FaPills,
  FluColds: FaLungs,
  VitaminsSupplements: FaCapsules,
  HygieneDisinfection: FaSprayCan,
  SkinHairNails: FaHandSparkles,
  FacialSkinCareMakeUp: FaPalette,
};

const TopCategoriesBar = React.memo(() => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, language } = useLanguage();
  const prefersReduced = useReducedMotion();
  const barRef = useRef(null);

  const fetchCategoriesData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchCategories();
      const allCategories = response.data || [];
      setCategories(allCategories);
    } catch (error) {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategoriesData();
  }, [fetchCategoriesData]);

  const displayCategories = useMemo(() => {
    if (!categories.length) return [];

    const categoryMap = new Map();
    categories.forEach((cat) => {
      categoryMap.set(cat.category.en, cat);
    });

    return Object.keys(categoryDisplayMap)
      .map((categoryEn) => {
        const category = categoryMap.get(categoryEn);
        if (!category) return null;

        return {
          ...category,
          // categoryDisplayMap selects/orders which categories appear; the
          // actual label is the localized taxonomy name (labels → de → key).
          displayName: pickCategoryName(category.category, language),
          icon: categoryIconMap[categoryEn],
        };
      })
      .filter(Boolean);
  }, [categories, language]);

  const handleCategoryClick = (category) => {
    const cleanCategoryId = category.category.en.trim();
    sessionStorage.setItem('germanName', category.category.de);
    navigate(`/categories/${cleanCategoryId}/products`);
  };

  const visible = !loading && displayCategories.length > 0;

  // Publish the band's height as --topbar-h (0 when absent or hidden on
  // phones), so sticky side panels can clear the whole sticky header.
  useEffect(() => {
    const root = document.documentElement;
    const el = barRef.current;
    if (!visible || !el) {
      root.style.setProperty('--topbar-h', '0px');
      return undefined;
    }
    const update = () => root.style.setProperty('--topbar-h', `${el.offsetHeight}px`);
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update, { passive: true });
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
      root.style.setProperty('--topbar-h', '0px');
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <motion.section
      ref={barRef}
      // Last band of the red header (Banner → Nav → this). The rounded foot
      // is where the header hands over to the white page, so the block ends
      // on a shape rather than a ruled edge.
      // Large screens only, on every page that renders it: on phones the
      // hero's category rail and the bottom tab bar cover this.
      className="relative z-[9990] hidden w-full rounded-b-[2.5rem] bg-nav pb-3 landing:bg-nav/80 landing:backdrop-blur-xl landing:backdrop-saturate-150 shadow-[0_18px_40px_-28px_rgb(var(--brand-shadow)/0.55)] lg:sticky lg:top-[var(--nav-h,0px)] lg:block"
      aria-label={t('home.moreInfo.title') || 'Beliebte Kategorien'}
      initial={{ opacity: 0, y: prefersReduced ? 0 : -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Desktop: centred, but on a real gap rather than the 4px the pills
          used to sit at. Deliberately not `justify-between` — that hands every
          leftover pixel to the gaps, so on a wide screen the six pills end up
          stranded at the extremes. A fixed gap keeps the row reading as one
          group at any width. */}
      {/* The chips sit in a frosted tray, so the row reads as its own band
          under the nav rather than loose text on the same red. */}
      <div className="flex w-full justify-center px-10 pt-1">
        <div className="inline-flex max-w-full flex-wrap items-center justify-center gap-1 rounded-full bg-nav-ink/[0.12] p-1.5 ring-1 ring-inset ring-nav-ink/20 landing:bg-white/70 landing:ring-nav-ink/10 shadow-[0_10px_30px_-18px_rgb(var(--brand-shadow-deep)/0.6)] backdrop-blur-sm xl:gap-2">
            {displayCategories.map((category, index) => {
              const Icon = category.icon;
              // Highlight the category the visitor is currently browsing
              // (its own page or one of its subcategories).
              const en = category.category.en.trim();
              const isActive = pathname.startsWith(`/categories/${en}/`) || pathname.startsWith(`/categories/${en}_`);
              return (
                <motion.button
                  key={category._id}
                  type="button"
                  onClick={() => handleCategoryClick(category)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`group inline-flex items-center gap-2 whitespace-nowrap rounded-full py-1.5 pl-1.5 pr-4 text-[14px] font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 xl:text-[15px] ${
                    isActive
                      ? 'bg-white text-brand shadow-[0_8px_20px_-10px_rgb(var(--brand-shadow-deep)/0.7)] landing:bg-brand landing:text-white'
                      : 'text-nav-ink/90 hover:bg-white hover:text-brand'
                  }`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                >
                  {Icon && (
                    <span className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200 ${
                      isActive ? 'bg-brand/10 text-brand landing:bg-white/20 landing:text-white' : 'bg-nav-ink/15 ring-1 ring-inset ring-nav-ink/15 group-hover:bg-brand/10 group-hover:text-brand group-hover:ring-transparent'
                    }`} aria-hidden="true">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {category.displayName}
                </motion.button>
              );
          })}
        </div>
      </div>
    </motion.section>
  );
});

TopCategoriesBar.displayName = 'TopCategoriesBar';

export default TopCategoriesBar;

